import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useRedisCacheCheck, useRedisCacheMutations } from "../../../hooks/useRedisCacheAdmin";

const RedisCacheLookup: React.FC = () => {
  const { t } = useTranslation();
  const [productId, setProductId] = useState("");
  const [retailer, setRetailer] = useState("amazon");
  const [searchTriggered, setSearchTriggered] = useState(false);

  const { data, isLoading, refetch } = useRedisCacheCheck(
    productId,
    retailer,
    searchTriggered
  );
  const { flushProduct } = useRedisCacheMutations();

  const handleSearch = () => {
    if (productId.trim()) {
      setSearchTriggered(true);
      refetch();
    }
  };

  const handleFlush = async () => {
    if (window.confirm(t("admin.redisCache.confirmFlushProduct", { productId, retailer }))) {
      await flushProduct.mutateAsync({ productId, retailer });
      refetch();
    }
  };

  const cacheCheck = data?.data;

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("admin.redisCache.lookupProduct")}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("admin.productCache.productId")}
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                placeholder={t("admin.redisCache.productIdPlaceholder")}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Select
                fullWidth
                value={retailer}
                onChange={(e) => setRetailer(e.target.value)}
              >
                <MenuItem value="amazon">Amazon</MenuItem>
                <MenuItem value="walmart">Walmart</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={!productId.trim()}
                sx={{ height: 56 }}
              >
                {t("common.search")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {searchTriggered && (
        <Card>
          <CardContent>
            {isLoading ? (
              <Typography>{t("common.loading")}</Typography>
            ) : cacheCheck ? (
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  {cacheCheck.existsInRedis ? (
                    <CheckCircleIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                  ) : (
                    <CancelIcon color="error" sx={{ fontSize: 40, mr: 2 }} />
                  )}
                  <Box>
                    <Typography variant="h5">
                      {cacheCheck.existsInRedis ? t("admin.redisCache.foundInRedis") : t("admin.redisCache.notInRedis")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.productCache.productId")}: {cacheCheck.productId} • {t("admin.productCache.retailer")}: {cacheCheck.retailer}
                    </Typography>
                  </Box>
                </Box>

                {cacheCheck.existsInRedis && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("admin.shippingMetadata.productTitle")}
                      </Typography>
                      <Typography variant="body1">
                        {cacheCheck.productTitle || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("admin.shippingMetadata.price")}
                      </Typography>
                      <Typography variant="body1">
                        {cacheCheck.price
                          ? `$${cacheCheck.price.toFixed(2)}`
                          : "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t("admin.redisCache.ttl")}
                      </Typography>
                      <Typography variant="body1">
                        {cacheCheck.ttlInfo || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleFlush}
                        sx={{ mt: 2 }}
                      >
                        {t("admin.redisCache.flushFromRedis")}
                      </Button>
                    </Grid>
                  </Grid>
                )}
              </Box>
            ) : (
              <Typography>{t("admin.redisCache.noDataAvailable")}</Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default RedisCacheLookup;

